import { FC, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { UploadFile } from '@mui/icons-material'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { documentoService } from '@/services/documentoService'
import { CurrencyField } from '@/components/common/CurrencyField'
import { formatarCnpj } from '@/utils/cnpj'
import {
  ConciliacaoNotaResponse,
  LancamentoComissao,
  NotaFiscalComissao,
  PoliticaNfResponse,
} from '@/types/comissao'
import { brl } from './estadoFinanceiro'
import { codigoErro, mensagemErroNota } from './errosNotaFiscal'

/** Tipo de entidade que a NF de comissão usa no módulo de documentos (ver entity `NotaFiscalComissao`). */
export const TIPO_ENTIDADE_NF_COMISSAO = 'NOTA_FISCAL_COMISSAO'

/**
 * AN-9 §1: o módulo de documentos aceita XML só para `NOTA_FISCAL_COMISSAO` (limite próprio);
 * o XML é lido por parser determinístico, o PDF por OCR + IA.
 */
export const EXTENSOES_ACEITAS = '.pdf,.xml'
export const TEXTO_FORMATOS = 'PDF ou XML (o XML é lido com mais precisão)'

/** Espera de digitação antes de pedir a conciliação ao backend. */
const ESPERA_CONCILIACAO_MS = 300

const novaChave = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

interface Props {
  aberto: boolean
  /** Comissões que ainda podem entrar numa nota (`disponivelParaNota`). */
  disponiveis: LancamentoComissao[]
  /** Linha pela qual o consultor clicou em "Emitir →" — já vem marcada. */
  preSelecionada?: LancamentoComissao | null
  onFechar: () => void
  /** Chamado depois de registrar (com ou sem o anexo ter dado certo). */
  onRegistrada: () => void
}

/**
 * "Emitir →" do Financeiro (Figma 26:339 / 37:2): registra a nota que o consultor emitiu no
 * sistema dele e sobe o arquivo. O upload é OBRIGATÓRIO — sem o documento o Financeiro não
 * tem o que conferir.
 *
 * AN-9 (L10 · Q36):
 * - o consultor DIGITA o valor da nota; soma, diferença e tolerância vêm de
 *   `POST /notas-fiscais/conciliacao` (pré-validação) — nada é somado na tela;
 * - tolerância e CNPJ do tomador vêm da política vigente (`GET /notas-fiscais/politica`);
 * - cada intenção de registrar leva uma `Idempotency-Key`: a MESMA requisição reenviada usa
 *   a mesma chave (o backend responde replay, sem duplicar); dados diferentes, chave nova;
 * - 409/422 do backend viram texto legível (`errosNotaFiscal.ts`).
 *
 * Ordem: registrar → subir o arquivo referenciando o id da nota → anexar o `documentoId`. Se
 * o upload falhar depois do registro, a aba Notas fiscais oferece "Anexar arquivo".
 */
const EmitirNotaDialog: FC<Props> = ({
  aberto,
  disponiveis,
  preSelecionada,
  onFechar,
  onRegistrada,
}) => {
  const [numero, setNumero] = useState('')
  const [dataEmissao, setDataEmissao] = useState(dayjs().format('YYYY-MM-DD'))
  const [valor, setValor] = useState(0)
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set())
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const [politica, setPolitica] = useState<PoliticaNfResponse | null>(null)
  const [conciliacao, setConciliacao] = useState<ConciliacaoNotaResponse | null>(null)
  const [conciliando, setConciliando] = useState(false)
  const [erroConciliacao, setErroConciliacao] = useState<string | null>(null)

  /** Intenção em curso: mesma assinatura → mesma chave até dar certo (AN-9 §5). */
  const intencao = useRef<{ assinatura: string; chave: string } | null>(null)

  useEffect(() => {
    if (!aberto) return
    setNumero('')
    setDataEmissao(dayjs().format('YYYY-MM-DD'))
    setValor(0)
    setSelecionadas(new Set(preSelecionada ? [preSelecionada.id] : []))
    setArquivo(null)
    setErro(null)
    setConciliacao(null)
    setErroConciliacao(null)
    let vivo = true
    const buscar = async () => {
      try {
        const p = await comissaoService.politicaNf()
        if (vivo) setPolitica(p ?? null)
      } catch {
        if (vivo) setPolitica(null) // sem a política a tela só não mostra a dica
      }
    }
    void buscar()
    return () => {
      vivo = false
    }
  }, [aberto, preSelecionada])

  const ids = Array.from(selecionadas).sort()
  const chaveConciliacao = `${valor}|${ids.join(',')}`

  // Pré-validação no backend sempre que valor ou seleção mudam (com espera de digitação).
  useEffect(() => {
    setConciliacao(null)
    setErroConciliacao(null)
    if (!aberto || valor <= 0 || ids.length === 0) {
      setConciliando(false)
      return
    }
    let vivo = true
    setConciliando(true)
    const t = setTimeout(() => {
      const conciliar = async () => {
        try {
          const r = await comissaoService.conciliarNota({ valor, lancamentoIds: ids })
          if (vivo) setConciliacao(r)
        } catch (e) {
          if (vivo) {
            setErroConciliacao(
              mensagemErroNota(e, 'Não foi possível conferir o valor agora; o registro confere de novo.')
            )
          }
        } finally {
          if (vivo) setConciliando(false)
        }
      }
      void conciliar()
    }, ESPERA_CONCILIACAO_MS)
    return () => {
      vivo = false
      clearTimeout(t)
    }
    // `ids` é derivado de `selecionadas`; a chave cobre valor + seleção.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, chaveConciliacao])

  const alternar = (id: string) => {
    setSelecionadas((prev) => {
      const novo = new Set(prev)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })
  }

  const foraDaTolerancia = conciliacao?.dentroDaTolerancia === false
  const podeRegistrar =
    !salvando &&
    !!numero.trim() &&
    selecionadas.size > 0 &&
    valor > 0 &&
    !!arquivo &&
    !foraDaTolerancia

  const registrar = async () => {
    if (!arquivo) {
      setErro('Anexe o arquivo da nota (PDF ou XML) para registrar.')
      return
    }
    setSalvando(true)
    setErro(null)
    const request = {
      numero: numero.trim(),
      dataEmissao,
      valor,
      lancamentoIds: ids,
    }
    const assinatura = JSON.stringify(request)
    if (!intencao.current || intencao.current.assinatura !== assinatura) {
      intencao.current = { assinatura, chave: novaChave() }
    }
    let nota: NotaFiscalComissao
    try {
      nota = await comissaoService.registrarNota(request, intencao.current.chave)
    } catch (e) {
      // Conflito de chave: a próxima tentativa é outra intenção.
      if (codigoErro(e) === 'IDEMPOTENCIA_CONFLITO') intencao.current = null
      setErro(mensagemErroNota(e, 'Falha ao registrar a nota.'))
      setSalvando(false)
      return
    }
    intencao.current = null
    // Replay de uma nota que já tem o arquivo: nada a subir de novo.
    if (!(nota.replay && nota.documentoId)) {
      try {
        const doc = await documentoService.upload(
          arquivo,
          'NOTA_FISCAL',
          nota.id,
          TIPO_ENTIDADE_NF_COMISSAO
        )
        await comissaoService.anexarDocumentoNota(nota.id, doc.id)
      } catch {
        // A nota já está registrada; o que faltou foi o anexo.
        setErro(
          'A nota foi registrada, mas o arquivo não foi anexado. Use "Anexar arquivo" na aba Notas fiscais.'
        )
        setSalvando(false)
        onRegistrada()
        return
      }
    }
    setSalvando(false)
    onRegistrada()
    onFechar()
  }

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Emitir nota fiscal</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <Typography variant="body2" color="textSecondary">
            Emita a nota no seu sistema, informe os dados aqui e anexe o arquivo. Marque as
            comissões que a nota cobre e digite o valor da nota. Não há valor mínimo.
          </Typography>
          {politica && (
            <Alert severity="info" data-testid="politica-nf">
              {politica.cnpjTomador ? (
                <>
                  Tomador da nota: <strong>{formatarCnpj(politica.cnpjTomador)}</strong>.{' '}
                </>
              ) : (
                <>O Financeiro ainda não cadastrou o CNPJ do tomador. </>
              )}
              Tolerância entre o valor da nota e as comissões:{' '}
              <strong>{brl(politica.toleranciaValor)}</strong>.
            </Alert>
          )}
          <TextField
            label="Número da nota"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            fullWidth
          />
          <TextField
            label="Data de emissão"
            type="date"
            value={dataEmissao}
            onChange={(e) => setDataEmissao(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Comissões cobertas
          </Typography>
          <Stack>
            {disponiveis.map((l) => (
              <FormControlLabel
                key={l.id}
                control={
                  <Checkbox checked={selecionadas.has(l.id)} onChange={() => alternar(l.id)} />
                }
                label={
                  <Typography variant="body2">
                    {dayjs(l.competencia).format('MM/YYYY')} · {l.razaoSocial ?? `RES-${l.numeroReserva ?? '—'}`} ·{' '}
                    <strong>{brl(l.valorEfetivo)}</strong>
                  </Typography>
                }
              />
            ))}
          </Stack>

          <CurrencyField
            label="Valor da nota"
            value={valor}
            onChange={setValor}
            fullWidth
            helperText="O valor que consta na nota emitida."
          />

          {conciliando && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} aria-label="Conferindo o valor" />
              <Typography variant="caption" color="textSecondary">
                Conferindo o valor com as comissões marcadas…
              </Typography>
            </Stack>
          )}
          {conciliacao && (
            <Alert
              severity={conciliacao.dentroDaTolerancia === false ? 'error' : 'success'}
              data-testid="conciliacao-nf"
            >
              Soma das comissões marcadas: <strong>{brl(conciliacao.somaComissoes)}</strong> ·
              diferença: <strong>{brl(conciliacao.diferenca)}</strong> · tolerância:{' '}
              <strong>{brl(conciliacao.toleranciaAplicada)}</strong>.{' '}
              {conciliacao.dentroDaTolerancia !== false
                ? 'Valor dentro da tolerância.'
                : 'Fora da tolerância: confira o valor digitado ou as comissões marcadas.'}
            </Alert>
          )}
          {erroConciliacao && <Alert severity="warning">{erroConciliacao}</Alert>}

          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFile />}
            sx={{ alignSelf: 'flex-start' }}
          >
            {arquivo ? arquivo.name : 'Anexar arquivo da nota (PDF ou XML)'}
            <input
              hidden
              type="file"
              accept={EXTENSOES_ACEITAS}
              aria-label="Arquivo da nota fiscal"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
          </Button>
          <Typography variant="caption" color="textSecondary">
            Obrigatório. {TEXTO_FORMATOS}. Depois do registro, os dados da nota (emitente,
            tomador, valores e impostos) são lidos do arquivo e conferidos pelo Financeiro.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Cancelar</Button>
        <Button variant="contained" onClick={() => void registrar()} disabled={!podeRegistrar}>
          {salvando ? 'Registrando…' : 'Registrar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmitirNotaDialog
